import { useState } from 'react'

import { ThemeModal } from '../theme/ThemeModal'
import { AnimatedBoard } from './AnimatedBoard'
import { ConnectionBanner } from './ConnectionBanner'
import { InProgressGameApi, PlayingGameApi } from './GameApi/InProgressGameApi'
import { useConnectionStatus } from './hooks/useConnectionStatus'
import {
  GameLogic,
  usePlayingGameLogic,
  useReadOnlyGameLogic,
} from './hooks/useGameController'
import { useNotifications } from './hooks/useNotifications'
import { Players } from './Players'

export function InProgressGame({ gameApi }: { gameApi: InProgressGameApi }) {
  if (gameApi.playable) {
    return <PlayingInner gameApi={gameApi as PlayingGameApi} />
  }
  return <ReadOnlyInner gameApi={gameApi} />
}

function PlayingInner({ gameApi }: { gameApi: PlayingGameApi }) {
  const logic = usePlayingGameLogic(gameApi)
  const { showNotificationStatus } = useNotifications(gameApi)
  return (
    <GameLayout
      gameApi={gameApi}
      logic={logic}
      showNotificationStatus={showNotificationStatus}
    />
  )
}

function ReadOnlyInner({ gameApi }: { gameApi: InProgressGameApi }) {
  const logic = useReadOnlyGameLogic(gameApi)
  const { showNotificationStatus } = useNotifications(gameApi)
  return (
    <GameLayout
      gameApi={gameApi}
      logic={logic}
      showNotificationStatus={showNotificationStatus}
    />
  )
}

function GameLayout({
  gameApi,
  logic,
  showNotificationStatus,
}: {
  gameApi: InProgressGameApi
  logic: GameLogic
  showNotificationStatus: () => void
}) {
  const {
    board,
    liveMoves,
    turn,
    turnKey,
    dice,
    dropTargets,
    hitTargets,
    selected,
    onLaneClicked,
    onEndTurn,
    endTurnEnabled,
    onUndo,
    undoEnabled,
    onRoll,
  } = logic

  const { status, opponentOnline } = useConnectionStatus(gameApi)
  const [themeOpen, setThemeOpen] = useState(false)

  return (
    <div className={`game ${gameApi.getRules()}`}>
      <div className="header">
        <button className="back" onClick={() => window.location.assign(`/`)}>
          Back
        </button>
        <button
          className="theme"
          aria-label="Theme"
          onClick={() => setThemeOpen(true)}
        >
          🎨
        </button>
        <button
          className="notifications"
          onClick={() => showNotificationStatus()}
        >
          Notifications
        </button>
      </div>
      {themeOpen && <ThemeModal onClose={() => setThemeOpen(false)} />}
      <ConnectionBanner status={status} />
      <Players
        players={gameApi.getPlayers()}
        turn={turn}
        dice={dice}
        onEndTurn={onEndTurn}
        endTurnEnabled={endTurnEnabled}
        onUndo={onUndo}
        undoEnabled={undoEnabled}
        onRoll={onRoll}
        opponentOnline={gameApi.isParticipant() ? opponentOnline : undefined}
      />
      <AnimatedBoard
        board={board}
        liveMoves={liveMoves}
        turnKey={turnKey}
        selected={selected}
        onLaneClicked={onLaneClicked}
        dropTargets={dropTargets}
        hitTargets={hitTargets}
      />
    </div>
  )
}
